import React from "react";
import VocImageGrid from "./VocImageGrid";
import useFilePreviews from "./useFilePreviews";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faLock,
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { categoryLabel, contactStatusLabel } from "../../services/voc";

// มือถือ: หัวข้อตัวหนา ค่าตัวเล็กสีเทา (ตาม design) / จอใหญ่: หัวข้อเล็กสีเทา ค่าตัวปกติ
const LABEL =
  "text-body font-bold text-slateGray sm:text-small sm:font-normal sm:text-metaGray";
const VALUE = "text-meta text-metaGray sm:text-body sm:text-slateGray";

/**
 * กล่องตรวจทานก่อนส่งจริง
 *
 * แสดงเฉพาะสิ่งที่กรอกมา ไม่โชว์ช่องว่าง จะได้อ่านง่าย
 */
export default function VocConfirmModal({
  open,
  value,
  files,
  lang,
  submitting,
  onCancel,
  onConfirm,
}) {
  // hook ต้องเรียกก่อน return ทุกครั้ง
  const previews = useFilePreviews(open ? files : []);

  if (!open) return null;

  const t = (th, en) => (lang === "th" ? th : en);

  const rows = [
    [t("ประเภทความคิดเห็น", "Type of feedback"), categoryLabel(value.category, lang)],
    [
      t("ชื่อ-นามสกุล หรือชื่อที่ท่านสะดวกให้เราเรียก", "Name"),
      value.fullName,
    ],
    ["Email", value.email],
    [
      t("สถานภาพปัจจุบันของท่าน", "You are"),
      contactStatusLabel(value.contactStatus, lang),
    ],
  ].filter(([, v]) => v);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-[16px]">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={submitting ? undefined : onCancel}
      />

      <div className="relative bg-white rounded-[12px] w-full max-w-[720px] max-h-[90vh] sm:max-h-[86vh] overflow-y-auto p-[16px] sm:p-[24px] xl:p-[32px]">
        <h2 className="text-title font-bold text-slateGray text-left">
          {t("ตรวจสอบข้อมูลก่อนส่ง", "Review before sending")}
        </h2>

        <div className="mt-[20px] flex flex-col gap-[14px]">
          {rows.map(([label, val]) => (
            <div key={label} className="text-left">
              <p className={LABEL}>{label}</p>
              <p className={VALUE}>{val}</p>
            </div>
          ))}

          <div className="text-left">
            <p className={LABEL}>
              {t("รายละเอียด", "Details")}
            </p>
            <p className={`${VALUE} whitespace-pre-line`}>
              {value.detail}
            </p>
          </div>

          {files.length > 0 && (
            <div className="text-left">
              <p className={`${LABEL} mb-[8px]`}>
                {t("รูปภาพประกอบ", "Images")} ({files.length})
              </p>
              <VocImageGrid
                images={previews}
                className="grid grid-cols-1 max-w-[260px] sm:max-w-none sm:grid-cols-3 gap-[8px] sm:gap-[10px]"
                lang={lang}
              />
            </div>
          )}

          <div className="text-left">
            <p className="flex items-center gap-[8px] text-meta font-bold text-blue mb-[8px]">
              <FontAwesomeIcon icon={faShieldHalved} />
              {t("การเผยแพร่ข้อมูล", "Publishing your feedback")}
            </p>
            <p className="flex items-start gap-[8px] text-meta text-slateGray">
              <FontAwesomeIcon
                icon={value.publicConsent ? faCircleCheck : faCircleXmark}
                className={
                  "mt-[5px] " + (value.publicConsent ? "text-blue" : "text-metaGray")
                }
              />
              <span>
                {value.publicConsent
                  ? t(
                      "ยินยอมให้เผยแพร่ความคิดเห็นของท่าน โดยปกปิดข้อมูลส่วนบุคคล",
                      "Consented — personal details will be hidden"
                    )
                  : t(
                      "ไม่ยินยอมให้เผยแพร่ความคิดเห็น",
                      "Did not consent to publication"
                    )}
              </span>
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[16px] mt-[24px]">
          <p className="flex items-center gap-[10px] text-meta text-slateGray text-left">
            <FontAwesomeIcon icon={faLock} className="text-metaGray" />
            {t(
              "ข้อมูลของท่านจะถูกเก็บเป็นความลับ และใช้เพื่อการพัฒนาการบริการเท่านั้น",
              "Your information stays confidential"
            )}
          </p>

          <div className="flex justify-end gap-[12px] shrink-0">
          <button
            type="button"
            disabled={submitting}
            onClick={onCancel}
            className="rounded-[8px] border-[1px] border-cardBorder text-slateGray text-body px-[28px] py-[10px] hover:bg-lightGray3 disabled:opacity-50"
          >
            {t("ย้อนกลับ", "Back")}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={onConfirm}
            className="rounded-[8px] bg-brand text-white text-body font-bold px-[28px] py-[10px] hover:opacity-90 disabled:opacity-50"
          >
            {submitting
              ? t("กำลังส่ง...", "Sending...")
              : t("ยืนยันข้อมูล", "Confirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
